import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSendNotification } from '../../hooks/useNotifications';
import { 
  NOTIFICATION_TYPES, 
  DEFAULT_NOTIFICATION,
  FILE_UPLOAD_CONFIG
} from '../../constants/notificationConstants';
import { 
  validateNotificationForm, 
  validateFiles, 
  sanitizeNotificationData 
} from '../../utils/notificationValidation';
import Button from '../ui/Button';
import Input from '../ui/Input';

const SendNotificationModal = ({ isOpen, onClose, selectedUsers = [], allUsers = [], committees = [] }) => {
  const { user } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const { sendNotification, uploadAttachment, updateNotificationAttachments, loading, error, clearError } = useSendNotification();
  
  // Two-step flow states
  const [currentStep, setCurrentStep] = useState(1);
  const [recipientType, setRecipientType] = useState('users');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectedCommittees, setSelectedCommittees] = useState([]);
  const [initialized, setInitialized] = useState(false);
  
  // Notification states
  const [activeTab, setActiveTab] = useState(NOTIFICATION_TYPES.GENERAL);
  const [formData, setFormData] = useState(DEFAULT_NOTIFICATION);
  const [errors, setErrors] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // Modal configuration
  const modalSize = getResponsiveValue({
    mobile: 'w-full h-full',
    tablet: 'w-11/12 max-w-4xl max-h-[90vh]',
    desktop: 'w-full max-w-5xl max-h-[90vh]'
  });

  const modalPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  // Initialize modal state when opened
  if (isOpen && !initialized) {
    console.log('Initializing modal state...');
    
    setCurrentStep(1);
    setRecipientType('users');
    setSelectedRecipients([]);
    setSelectedCommittees([]);
    setFormData(DEFAULT_NOTIFICATION);
    setErrors({});
    setAttachments([]);
    setActiveTab(NOTIFICATION_TYPES.GENERAL);
    setInitialized(true);
    
    if (selectedUsers && selectedUsers.length > 0) {
      const userIds = selectedUsers.map(user => user.uid || user.id);
      console.log('Pre-selecting users:', userIds);
      setSelectedRecipients(userIds);
    }
    
    clearError();
  }

  // Reset when modal closes
  if (!isOpen && initialized) {
    console.log('Resetting modal state...');
    setInitialized(false);
    setCurrentStep(1);
    setRecipientType('users');
    setSelectedRecipients([]);
    setSelectedCommittees([]);
    setFormData(DEFAULT_NOTIFICATION);
    setErrors({});
    setAttachments([]);
    setActiveTab(NOTIFICATION_TYPES.GENERAL);
  }

  // Event handlers
  const handleRecipientTypeChange = (type) => {
    console.log('Changing recipient type to:', type);
    setRecipientType(type);
    setSelectedRecipients([]);
    setSelectedCommittees([]);
    setErrors(prev => ({ ...prev, recipients: null }));
  };

  const handleUserSelection = (userId) => {
    console.log('User selection clicked:', userId);
    
    setSelectedRecipients(prev => {
      const newSelection = prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId];
      
      console.log('Updated user selection:', newSelection);
      return newSelection;
    });
    
    setErrors(prev => ({ ...prev, recipients: null }));
  };

  const handleCommitteeSelection = (committeeId) => {
    console.log('Committee selection clicked:', committeeId);
    
    setSelectedCommittees(prev => {
      const newSelection = prev.includes(committeeId)
        ? prev.filter(id => id !== committeeId)
        : [...prev, committeeId];
      
      console.log('Updated committee selection:', newSelection);
      return newSelection;
    });
    
    setErrors(prev => ({ ...prev, recipients: null }));
  };

  const handleSelectAll = () => {
    console.log('Select All clicked for:', recipientType);
    
    if (recipientType === 'users') {
      const allUserIds = allUsers.map(user => user.uid || user.id);
      console.log('Selecting all users:', allUserIds);
      setSelectedRecipients(allUserIds);
    } else {
      const allCommitteeIds = committees.map(committee => committee.id);
      console.log('Selecting all committees:', allCommitteeIds);
      setSelectedCommittees(allCommitteeIds);
    }
  };

  const handleClearAll = () => {
    console.log('Clear All clicked');
    setSelectedRecipients([]);
    setSelectedCommittees([]);
  };

  const proceedToNotification = () => {
    console.log('Proceeding to notification step');
    const hasRecipients = (recipientType === 'users' && selectedRecipients.length > 0) ||
                         (recipientType === 'committees' && selectedCommittees.length > 0);
    
    if (!hasRecipients) {
      setErrors({ recipients: 'Please select at least one recipient' });
      return;
    }

    let finalRecipientIds = [];
    
    if (recipientType === 'users') {
      finalRecipientIds = selectedRecipients;
    } else {
      finalRecipientIds = committees
        .filter(committee => selectedCommittees.includes(committee.id))
        .flatMap(committee => committee.members || [])
        .filter((userId, index, array) => array.indexOf(userId) === index);
    }

    console.log('Final recipient IDs:', finalRecipientIds);

    setFormData(prev => ({ 
      ...prev, 
      recipientIds: finalRecipientIds,
      committeeIds: recipientType === 'committees' ? selectedCommittees : []
    }));
    
    setCurrentStep(2);
    setErrors({});
  };

  const goBackToRecipients = () => {
    console.log('Going back to recipients');
    setCurrentStep(1);
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleTabChange = (newType) => {
    setActiveTab(newType);
    setFormData(prev => ({ ...prev, type: newType }));
  };

  const handleFileUpload = async (files) => {
    if (files.length === 0) return;

    const fileValidation = validateFiles(Array.from(files));
    if (!fileValidation.isValid) {
      setErrors(prev => ({ ...prev, files: fileValidation.errors.join(', ') }));
      return;
    }

    setUploadingFiles(true);
    setErrors(prev => ({ ...prev, files: null }));

    try {
      const fileObjects = Array.from(files).map(file => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: URL.createObjectURL(file)
      }));
      
      setAttachments(prev => [...prev, ...fileObjects]);
      setFormData(prev => ({ 
        ...prev, 
        attachments: [...(prev.attachments || []), ...fileObjects] 
      }));
    } catch (err) {
      setErrors(prev => ({ ...prev, files: 'Failed to process files' }));
    } finally {
      setUploadingFiles(false);
    }
  };

  const removeAttachment = (index) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(newAttachments);
    setFormData(prev => ({ ...prev, attachments: newAttachments }));
  };

  const handleSubmit = async () => {
    try {
      const sanitizedData = sanitizeNotificationData(formData);
      const validation = validateNotificationForm(sanitizedData);
      
      if (!validation.isValid) {
        setErrors(validation.errors);
        return;
      }

      const { attachments: _, ...notificationDataWithoutFiles } = sanitizedData;
      
      const notificationWithSender = {
        ...notificationDataWithoutFiles,
        senderId: user.uid || user.id
      };

      console.log('Sending notification with data:', notificationWithSender);

      const sentNotification = await sendNotification(notificationWithSender);
      
      if (attachments.length > 0) {
        console.log('Uploading attachments for notification:', sentNotification.id);
        
        const uploadPromises = attachments.map(fileObj => 
          uploadAttachment(fileObj.file, sentNotification.id)
        );
        
        const uploadedFiles = await Promise.all(uploadPromises);
        await updateNotificationAttachments(sentNotification.id, uploadedFiles);
      }

      onClose();
    } catch (err) {
      console.error('Failed to send notification:', err);
    }
  };

  const getSelectedCount = () => {
    if (recipientType === 'users') {
      return selectedRecipients.length;
    } else {
      const totalUsers = committees
        .filter(committee => selectedCommittees.includes(committee.id))
        .reduce((total, committee) => total + (committee.members?.length || 0), 0);
      return totalUsers;
    }
  };

  const getSelectedItems = () => {
    if (recipientType === 'users') {
      return allUsers.filter(user => selectedRecipients.includes(user.uid || user.id));
    } else {
      return committees.filter(committee => selectedCommittees.includes(committee.id));
    }
  };

  // Helper to get initials from fullName
  const getInitials = (fullName) => {
    if (!fullName) return '?';
    const names = fullName.trim().split(' ');
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-bg-overlay z-50 flex items-center justify-center">
      <div className={`${modalSize} bg-surface-elevated rounded-xl shadow-theme-lg border border-border-primary overflow-hidden`}>
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-primary">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-text-primary">Send Notification</h2>
            {/* Step Indicator */}
            <div className="flex items-center space-x-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                currentStep === 1 ? 'bg-accent text-white' : 'bg-success text-white'
              }`}>
                {currentStep === 1 ? '1' : '✓'}
              </div>
              <div className={`w-8 h-0.5 ${currentStep === 2 ? 'bg-accent' : 'bg-border-secondary'}`}></div>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                currentStep === 2 ? 'bg-accent text-white' : 'bg-border-secondary text-text-tertiary'
              }`}>
                2
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-tertiary hover:text-text-primary hover:bg-surface-hover rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className={`${modalPadding} overflow-y-auto`} style={{ maxHeight: '70vh' }}>
          {error && (
            <div className="mb-4 p-3 bg-error-subtle text-error rounded-lg border border-border-error">
              {error}
            </div>
          )}

          {/* Step 1: Recipients Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-text-primary mb-4">Select Recipients</h3>
                
                {/* Recipient Type Toggle */}
                <div className="flex space-x-1 bg-surface-secondary rounded-lg p-1 mb-4">
                  <button
                    type="button"
                    onClick={() => handleRecipientTypeChange('users')}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      recipientType === 'users'
                        ? 'bg-surface-elevated text-text-primary shadow-theme-sm'
                        : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
                    }`}
                  >
                    <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Individual Users ({allUsers.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRecipientTypeChange('committees')}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      recipientType === 'committees'
                        ? 'bg-surface-elevated text-text-primary shadow-theme-sm'
                        : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
                    }`}
                  >
                    <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.121M7 20v-2c0-.656.126-1.283.356-1.857M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    Committees ({committees.length})
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between items-center mb-4">
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearAll}
                    >
                      Clear All
                    </Button>
                  </div>
                  <div className="text-sm text-text-secondary">
                    {getSelectedCount()} recipients selected
                  </div>
                </div>

                {/* Users Selection */}
                {recipientType === 'users' && (
                  <div className="space-y-2 max-h-96 overflow-y-auto border border-border-primary rounded-lg">
                    {allUsers.length === 0 ? (
                      <div className="p-4 text-center text-text-secondary">
                        No users available
                      </div>
                    ) : (
                      allUsers.map(user => {
                        const userId = user.uid || user.id;
                        const isSelected = selectedRecipients.includes(userId);
                        
                        return (
                          <div
                            key={userId}
                            className="flex items-center space-x-3 p-3 hover:bg-surface-hover transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleUserSelection(userId);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-accent rounded border-border-secondary focus:ring-accent cursor-pointer"
                            />
                            <div 
                              onClick={() => handleUserSelection(userId)}
                              className="flex items-center space-x-3 flex-1 cursor-pointer"
                            >
                              {user.profilePicture ? (
                                <img 
                                  src={user.profilePicture} 
                                  alt={user.fullName}
                                  className="w-8 h-8 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-navigation rounded-lg flex items-center justify-center">
                                  <span className="text-navigation-text text-xs font-medium">
                                    {getInitials(user.fullName)}
                                  </span>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-text-primary">
                                  {user.fullName}
                                </div>
                                <div className="text-sm text-text-secondary truncate">
                                  {user.email} • {user.department}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {/* Committees Selection */}
                {recipientType === 'committees' && (
                  <div className="space-y-2 max-h-96 overflow-y-auto border border-border-primary rounded-lg">
                    {committees.length === 0 ? (
                      <div className="p-4 text-center text-text-secondary">
                        No committees available
                      </div>
                    ) : (
                      committees.map(committee => {
                        const isSelected = selectedCommittees.includes(committee.id);
                        
                        return (
                          <div
                            key={committee.id}
                            className="flex items-center space-x-3 p-3 hover:bg-surface-hover transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleCommitteeSelection(committee.id);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-accent rounded border-border-secondary focus:ring-accent cursor-pointer"
                            />
                            <div 
                              onClick={() => handleCommitteeSelection(committee.id)}
                              className="flex-1 cursor-pointer"
                            >
                              <div className="font-medium text-text-primary">
                                {committee.name}
                              </div>
                              <div className="text-sm text-text-secondary">
                                {committee.members?.length || 0} members
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {errors.recipients && (
                  <p className="mt-2 text-sm text-error">{errors.recipients}</p>
                )}
              </div>

              {/* Selected Summary */}
              {(selectedRecipients.length > 0 || selectedCommittees.length > 0) && (
                <div className="bg-surface-secondary rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-2">Selected ({getSelectedCount()} recipients):</h4>
                  <div className="flex flex-wrap gap-2">
                    {getSelectedItems().map(item => (
                      <span
                        key={item.id || item.uid}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent-subtle text-accent"
                      >
                        {recipientType === 'users' 
                          ? item.fullName
                          : `${item.name} (${item.members?.length || 0} members)`
                        }
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Notification Type Selection */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* Back Button and Summary */}
              <div className="flex items-center justify-between">
                <button
                  onClick={goBackToRecipients}
                  className="flex items-center space-x-2 text-accent hover:text-accent-hover"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span>Back to Recipients</span>
                </button>
                <div className="text-sm text-text-secondary">
                  Sending to {getSelectedCount()} recipients
                </div>
              </div>

              {/* Notification Type Tabs */}
              <div>
                <h3 className="text-lg font-medium text-text-primary mb-4">Choose Notification Type</h3>
                <div className="flex border-b border-border-primary bg-surface-secondary rounded-t-lg">
                  {[
                    { id: NOTIFICATION_TYPES.GENERAL, label: 'General' },
                    { id: NOTIFICATION_TYPES.INVITATION, label: 'Invitation' },
                    { id: NOTIFICATION_TYPES.MEETING, label: 'Meeting' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-colors ${
                        activeTab === tab.id
                          ? 'text-accent border-b-2 border-accent bg-surface-elevated'
                          : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
                      }`}
                    >
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="bg-surface-elevated border border-border-primary rounded-b-lg p-6 space-y-4">
                {activeTab === NOTIFICATION_TYPES.GENERAL && (
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Message *
                    </label>
                    <textarea
                      value={formData.content || ''}
                      onChange={(e) => handleInputChange('content', e.target.value)}
                      rows={6}
                      className={`input-base w-full ${errors.content ? 'border-border-error' : ''}`}
                      placeholder="Enter your message..."
                    />
                    {errors.content && (
                      <p className="mt-1 text-sm text-error">{errors.content}</p>
                    )}
                  </div>
                )}

                {activeTab === NOTIFICATION_TYPES.INVITATION && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Invitation Message *
                      </label>
                      <textarea
                        value={formData.content || ''}
                        onChange={(e) => handleInputChange('content', e.target.value)}
                        rows={4}
                        className={`input-base w-full ${errors.content ? 'border-border-error' : ''}`}
                        placeholder="Enter invitation message..."
                      />
                      {errors.content && (
                        <p className="mt-1 text-sm text-error">{errors.content}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Venue *
                      </label>
                      <Input
                        value={formData.venue || ''}
                        onChange={(e) => handleInputChange('venue', e.target.value)}
                        placeholder="Enter venue"
                        className={errors.venue ? 'border-border-error' : ''}
                      />
                      {errors.venue && (
                        <p className="mt-1 text-sm text-error">{errors.venue}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Event Date *
                        </label>
                        <Input
                          type="date"
                          value={formData.eventDate || ''}
                          onChange={(e) => handleInputChange('eventDate', e.target.value)}
                          className={errors.eventDate ? 'border-border-error' : ''}
                        />
                        {errors.eventDate && (
                          <p className="mt-1 text-sm text-error">{errors.eventDate}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Event Time *
                        </label>
                        <Input
                          type="time"
                          value={formData.eventTime || ''}
                          onChange={(e) => handleInputChange('eventTime', e.target.value)}
                          className={errors.eventTime ? 'border-border-error' : ''}
                        />
                        {errors.eventTime && (
                          <p className="mt-1 text-sm text-error">{errors.eventTime}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === NOTIFICATION_TYPES.MEETING && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Meeting Heading *
                      </label>
                      <Input
                        value={formData.meetingHeading || ''}
                        onChange={(e) => handleInputChange('meetingHeading', e.target.value)}
                        placeholder="Enter meeting heading"
                        className={errors.meetingHeading ? 'border-border-error' : ''}
                      />
                      {errors.meetingHeading && (
                        <p className="mt-1 text-sm text-error">{errors.meetingHeading}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Chaired By *
                      </label>
                      <Input
                        value={formData.chairedBy || ''}
                        onChange={(e) => handleInputChange('chairedBy', e.target.value)}
                        placeholder="Enter chairperson name"
                        className={errors.chairedBy ? 'border-border-error' : ''}
                      />
                      {errors.chairedBy && (
                        <p className="mt-1 text-sm text-error">{errors.chairedBy}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Venue *
                      </label>
                      <Input
                        value={formData.venue || ''}
                        onChange={(e) => handleInputChange('venue', e.target.value)}
                        placeholder="Enter venue"
                        className={errors.venue ? 'border-border-error' : ''}
                      />
                      {errors.venue && (
                        <p className="mt-1 text-sm text-error">{errors.venue}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Date *
                        </label>
                        <Input
                          type="date"
                          value={formData.eventDate || ''}
                          onChange={(e) => handleInputChange('eventDate', e.target.value)}
                          className={errors.eventDate ? 'border-border-error' : ''}
                        />
                        {errors.eventDate && (
                          <p className="mt-1 text-sm text-error">{errors.eventDate}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Time *
                        </label>
                        <Input
                          type="time"
                          value={formData.eventTime || ''}
                          onChange={(e) => handleInputChange('eventTime', e.target.value)}
                          className={errors.eventTime ? 'border-border-error' : ''}
                        />
                        {errors.eventTime && (
                          <p className="mt-1 text-sm text-error">{errors.eventTime}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Description *
                      </label>
                      <textarea
                        value={formData.description || ''}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows={4}
                        className={`input-base w-full ${errors.description ? 'border-border-error' : ''}`}
                        placeholder="Enter meeting description..."
                      />
                      {errors.description && (
                        <p className="mt-1 text-sm text-error">{errors.description}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* File Upload Section */}
                <div className="pt-6 border-t border-border-primary">
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Attachments (Optional)
                  </label>
                  <p className="text-xs text-text-secondary mb-3">
                    Upload images, PDFs, or Excel files (max {FILE_UPLOAD_CONFIG.maxFiles} files, {FILE_UPLOAD_CONFIG.maxSize / (1024 * 1024)}MB each)
                  </p>
                  
                  <div className="border-2 border-dashed border-border-secondary rounded-lg p-4 text-center">
                    <input
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.xls,.xlsx"
                      onChange={(e) => handleFileUpload(e.target.files)}
                      className="hidden"
                      id="file-upload"
                      disabled={uploadingFiles}
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <div className="flex flex-col items-center">
                        <svg className="w-8 h-8 text-text-tertiary mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="text-sm text-text-secondary">
                          {uploadingFiles ? 'Processing files...' : 'Click to upload files'}
                        </span>
                      </div>
                    </label>
                  </div>

                  {errors.files && (
                    <p className="mt-2 text-sm text-error">{errors.files}</p>
                  )}

                  {/* Uploaded Files */}
                  {attachments.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-surface-secondary rounded-lg">
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            <span className="text-sm text-text-primary">{file.name}</span>
                            <span className="text-xs text-text-tertiary">
                              ({(file.size / 1024).toFixed(1)}KB)
                            </span>
                          </div>
                          <button
                            onClick={() => removeAttachment(index)}
                            className="p-1 text-text-tertiary hover:text-error rounded"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-border-primary bg-surface-secondary">
          {currentStep === 1 ? (
            <>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={proceedToNotification}
                disabled={loading}
              >
                Next: Create Notification
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={goBackToRecipients}
                disabled={loading}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </Button>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                loading={loading}
              >
                Send Notification
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendNotificationModal;